import React, { useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { BaseUploadNodeProps } from "imagica-uikit/dist/nodesV2/typing";
import { useSize } from "ahooks";
import { effect, useSignal } from "@preact/signals-react";
import styles from "./PDFPreview.module.scss";
import { PDFPrev } from "./PDFPrev";
import { PDFNext } from "./PDFNext";
import cls from "classnames";
import VirtualList from "rc-virtual-list";
import { List } from "antd";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;
class DataItem {
  constructor(public index: number) {}
}
class PageSize {
  constructor(public originalWidth?: number, public originalHeight?: number) {}
}

export function PDFPreview({ createBlobUrl, noWheelClassName }: BaseUploadNodeProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const contentSize = useSize(contentRef);

  const numPages = useSignal<number>(0);
  const pageNumber = useSignal<number>(1);

  /// pdf 当前尺寸及原始尺寸
  const pageSize = useSignal<PageSize>(new PageSize());
  const pageScale = useSignal<number>(0.3);
  const previewDataList = useSignal(new Array<DataItem>());
  const documentLoaded = useSignal<boolean>(false);

  const noWheelContainerClass = cls(styles.container, noWheelClassName);
  useEffect(() => {
    previewDataList.value = Array.from({ length: numPages.value }, (val, i) => new DataItem(i + 1));
  }, [numPages.value]);
  effect(() => {
    if (!pageSize.value.originalHeight) {
      return;
    }
    if (!pageSize.value.originalWidth) {
      return;
    }
    if (!contentSize) {
      return;
    }

    const contentWidth = contentSize.width - 20 > 250 ? contentSize.width - 20 : 250;
    const contentHeight = contentSize.height - 20 > 250 ? contentSize.height - 20 : 250;

    /// 高 > 宽
    if (pageSize.value.originalHeight > pageSize.value.originalWidth) {
      let scale = contentHeight / pageSize.value.originalHeight;
      if (pageSize.value.originalWidth * scale > contentWidth) {
        scale = contentWidth / pageSize.value.originalWidth;
      }
      pageScale.value = scale;
    } else {
      let scale = contentWidth / pageSize.value.originalWidth;
      if (pageSize.value.originalHeight * scale > contentHeight) {
        scale = contentHeight / pageSize.value.originalHeight;
      }
      pageScale.value = scale;
    }
  });

  function onPageLoadError(error: Error) {
    console.log("load error", error);
  }

  function onPageLoadSuccess(originalWidth: number, originalHeight: number) {
    pageSize.value = new PageSize(originalWidth, originalHeight);
  }

  function onDocumentLoadError(error: Error) {
    console.log("load error", error);
  }

  function onDocumentLoadSuccess(pages: number) {
    documentLoaded.value = true;
    numPages.value = pages;
  }

  function handleDocumentPrev() {
    const prevPage = pageNumber.value - 1;
    if (prevPage < 1) {
      return;
    }
    pageNumber.value = prevPage;
  }

  function handleDocumentNext() {
    const nextPage = pageNumber.value + 1;
    if (nextPage > numPages.value) {
      return;
    }
    pageNumber.value = nextPage;
  }

  function handlePageNumClicked(num: number) {
    pageNumber.value = num;
  }
  function renderItem(item: DataItem) {
    if (!createBlobUrl) {
      return <div className={styles.container}>Upload error</div>;
    }
    return (
      <div
        key={item.index}
        className={`${styles.pdfPreviewItem} ${
          pageNumber.value === item.index ? styles.pdfPreviewItemSelected : styles.pdfPreviewItemUnselected
        }`}
        onClick={() => handlePageNumClicked(item.index)}
      >
        <Document key={item.index} file={createBlobUrl.fileBlob}>
          <Page
            pageNumber={item.index}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            loading={<div className={styles.pdfLoading}>Loading</div>}
            width={60}
          />
        </Document>
      </div>
    );
  }

  if (!createBlobUrl) {
    return <div className={styles.container}>Upload error</div>;
  }
  return (
    // <div className={`${style['custom-node']} ${props.isLongPress || props.checkNodeArr.includes(props.id) ? 'nowheel nopan' : ''}`}>
    <div className={noWheelContainerClass} key={createBlobUrl.objectURL}>
      <div className={styles.pdfContainer}>
        <div className={styles.previewBox} style={{ height: `${contentSize?.height ?? 0}px` }}>
          <div className={styles.pdfPreview}>
            {previewDataList.value.length > 0 && (
              <List>
                <VirtualList
                  data={previewDataList.value}
                  height={contentSize?.height ?? 0}
                  itemHeight={100}
                  itemKey="index"
                  onScroll={() => {}}
                >
                  {item => <List.Item key={item.index}>{renderItem(item)}</List.Item>}
                </VirtualList>
              </List>
            )}
          </div>
        </div>
        <div className={styles.contentBox} ref={contentRef}>
          <div className={styles.pdfContent}>
            <Document
              file={createBlobUrl.fileBlob}
              onLoadSuccess={(document: any) => onDocumentLoadSuccess(document.numPages)}
              onLoadError={(error: Error) => onDocumentLoadError(error)}
              className={styles.pdfDocument}
            >
              <Page
                pageNumber={pageNumber.value}
                className={styles.pdfPage}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<div className={styles.pdfLoading}>Loading</div>}
                scale={pageScale.value}
                onLoadError={(error: Error) => onPageLoadError(error)}
                onLoadSuccess={(page: any) => onPageLoadSuccess(page.originalWidth, page.originalHeight)}
              />
            </Document>
          </div>
          <div
            className={`${styles.pdfPagePane} ${
              documentLoaded.value ? styles.pdfPagePaneDisplay : styles.pdfPagePaneHidden
            }`}
          >
            <div className={styles.pdfPrev} onClick={() => handleDocumentPrev()}>
              <PDFPrev stroke={pageNumber.value <= 1 ? "#020C261A" : "#12192B"} />
            </div>
            <div className={styles.pdfPageNum}>
              {pageNumber.value} / {numPages.value}
            </div>
            <div className={styles.pdfNext} onClick={() => handleDocumentNext()}>
              <PDFNext stroke={pageNumber.value >= numPages.value ? "#020C261A" : "#12192B"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
